/**
 * Local dev API proxy utilities for control and API gateway backends.
 *
 * Why this exists:
 * When the frontend runs on https://dev-redroses.fnpplus.com:3000 and calls
 * https://uat-new.fnp.com/control/* or https://apigw-uat-r2.fnp.com/* directly,
 * the browser treats the request as cross-site. Set-Cookie headers from the
 * backend are blocked or stored for the wrong domain, so auth cookies never
 * attach to subsequent requests.
 *
 * Next.js routes at /control/* and /apigw/serviceability/* (POST only) proxy those calls
 * server-side on local dev. The browser only talks to dev-redroses.fnpplus.com
 * (same-site). Each proxy forwards to the real backend, rewrites Set-Cookie
 * Domain attributes, and returns cookies scoped to the local host.
 */

const PROXY_FORWARD_HEADERS = [
  "content-type",
  "accept",
  "authorization",
  "cookie",
  "user-agent",
  "x-api-version",
  "x-forwarded-cookie",
  "x-device-type",
  "x-roletype",
];

/** Serviceability paths that send `x-roletype` when agent or supplier flow is enabled. */
const SERVICEABILITY_ROLE_PATHS = [
  "/serviceability/v1/complete/cart-items",
  "/serviceability/v1/product/delivery-dates",
  "/serviceability/v1/earliest/slot",
  "/serviceability/v1/shipping-details",
  "/serviceability/v1/products/serviceable",
  "/serviceability/v1/reset-deliveries",
  "/serviceability/v1/plp",
];

/** Supplier-only paths that send `x-roletype: SUPPLIER` when supplier flow is enabled. */
const SUPPLIER_ONLY_ROLE_PATHS = ["/productapi/api/rest/v1.3/getDeliveryDateAndGeoPrice"];

const PROXY_RESPONSE_HEADERS = ["content-type", "cache-control", "pragma", "expires", "location", "x-frame-options"];

/** Cookie names the API gateway accepts via `x-forwarded-cookie` on local dev. */
const GATEWAY_FORWARDED_AUTH_COOKIES = new Set(["faul", "JSESSIONID"]);

/**
 * @returns {boolean}
 */
export function isLocalMachine() {
  return process.env.NEXT_PUBLIC_IS_LOCAL_MACHINE === "true";
}

/**
 * Returns the browser-facing base URL for control APIs on local dev.
 * Client-side calls use same-origin paths (e.g. /control/vc-rj) that hit the proxy.
 * Server-side rendering keeps calling uat-new.fnp.com directly.
 *
 * @returns {string}
 */
export function getWebApiBaseUrl() {
  const backendBaseUrl = process.env.NEXT_PUBLIC_BASE_URL || "";

  if (isLocalMachine() && typeof window !== "undefined") {
    return "";
  }

  return backendBaseUrl;
}

const SERVICEABILITY_PATH_PREFIX = "/serviceability";

/**
 * @returns {boolean}
 */
export function isAgentFlowEnabled() {
  return process.env.NEXT_PUBLIC_IS_AGENT_FLOW_ENABLED === "true";
}

/**
 * @returns {boolean}
 */
export function isSupplierFlowEnabled() {
  return process.env.NEXT_PUBLIC_IS_SUPPLIER_FLOW_ENABLED === "true";
}

/**
 * @param {string} [url]
 * @returns {string}
 */
function normalizeRequestPath(url = "") {
  const pathOnly = String(url).split("?")[0] || "";
  return pathOnly.replace(/^\/apigw/, "");
}

/**
 * @param {string} normalizedPath
 * @param {string[]} rolePaths
 * @returns {boolean}
 */
function matchesRolePath(normalizedPath, rolePaths) {
  return rolePaths.some((rolePath) => normalizedPath === rolePath || normalizedPath.startsWith(`${rolePath}/`));
}

/**
 * Whether the request URL targets a shared serviceability path for role headers.
 *
 * @param {string} [url]
 * @returns {boolean}
 */
function isServiceabilityRolePath(url = "") {
  return matchesRolePath(normalizeRequestPath(url), SERVICEABILITY_ROLE_PATHS);
}

/**
 * Whether the request URL targets a supplier-only path for role headers.
 *
 * @param {string} [url]
 * @returns {boolean}
 */
function isSupplierOnlyRolePath(url = "") {
  return matchesRolePath(normalizeRequestPath(url), SUPPLIER_ONLY_ROLE_PATHS);
}

/**
 * @param {import("axios").InternalAxiosRequestConfig} config
 * @param {string} roleType
 * @returns {import("axios").InternalAxiosRequestConfig}
 */
function setRoleTypeHeader(config, roleType) {
  const headers = config.headers || {};

  if (typeof headers.set === "function") {
    headers.set("x-roletype", roleType);
  } else {
    headers["x-roletype"] = roleType;
  }

  config.headers = headers;
  return config;
}

/**
 * Attaches `x-roletype` for selected serviceability APIs and supplier-only APIs.
 * Agent flow takes precedence on shared serviceability paths when both flows are enabled.
 *
 * @param {import("axios").InternalAxiosRequestConfig} config
 * @returns {import("axios").InternalAxiosRequestConfig}
 */
export function attachServiceabilityRoleHeader(config) {
  const requestUrl = config?.url || "";

  if (isAgentFlowEnabled() && isServiceabilityRolePath(requestUrl)) {
    return setRoleTypeHeader(config, "AGENT");
  }

  if (isSupplierFlowEnabled() && (isServiceabilityRolePath(requestUrl) || isSupplierOnlyRolePath(requestUrl))) {
    return setRoleTypeHeader(config, "SUPPLIER");
  }

  return config;
}

/**
 * @param {string} baseUrl
 * @param {string} gatewayBaseUrl
 * @returns {boolean}
 */
function isGatewayBaseUrl(baseUrl, gatewayBaseUrl) {
  if (!baseUrl || !gatewayBaseUrl) {
    return false;
  }

  try {
    return new URL(baseUrl).origin === new URL(gatewayBaseUrl).origin;
  } catch {
    return baseUrl.replace(/\/$/, "") === gatewayBaseUrl.replace(/\/$/, "");
  }
}

/**
 * Keeps only gateway auth cookies from a cookie header string.
 *
 * @param {string|undefined} cookieStr
 * @returns {string|undefined}
 */
function filterCookiesForGatewayAuth(cookieStr) {
  if (!cookieStr || typeof cookieStr !== "string") {
    return undefined;
  }

  const parts = [];
  for (const segment of cookieStr.split(";")) {
    const trimmed = segment.trim();
    if (!trimmed) {
      continue;
    }

    const eq = trimmed.indexOf("=");
    const name = (eq === -1 ? trimmed : trimmed.slice(0, eq)).trim();
    if (GATEWAY_FORWARDED_AUTH_COOKIES.has(name)) {
      parts.push(trimmed);
    }
  }

  return parts.length > 0 ? parts.join("; ") : undefined;
}

/**
 * Builds `x-forwarded-cookie` for upstream gateway calls from the proxied request.
 *
 * @param {Request} request
 * @returns {string|undefined}
 */
function buildGatewayForwardedCookieHeader(request) {
  const existing = request.headers.get("x-forwarded-cookie");
  if (existing) {
    return existing;
  }

  return filterCookiesForGatewayAuth(request.headers.get("cookie") || undefined);
}

/**
 * Attaches gateway auth headers on the browser request before it hits the local proxy.
 *
 * @param {import("axios").InternalAxiosRequestConfig} config
 */
function attachGatewayAuthHeadersForLocalProxy(config) {
  if (typeof document === "undefined") {
    return;
  }

  const headers = config.headers || {};
  const existing =
    typeof headers.get === "function"
      ? headers.get("x-forwarded-cookie") || headers.get("X-Forwarded-Cookie")
      : headers["x-forwarded-cookie"] || headers["X-Forwarded-Cookie"];

  if (existing) {
    return;
  }

  const authCookies = filterCookiesForGatewayAuth(document.cookie);
  if (!authCookies) {
    return;
  }

  if (typeof headers.set === "function") {
    headers.set("x-forwarded-cookie", authCookies);
    return;
  }

  config.headers = { ...headers, "x-forwarded-cookie": authCookies };
}

/**
 * On local CSR, rewrites gateway POST /serviceability/* axios requests to same-origin
 * /apigw/serviceability/* so only POST serviceability APIs hit the proxy.
 *
 * @param {import("axios").InternalAxiosRequestConfig} config
 * @returns {import("axios").InternalAxiosRequestConfig}
 */
export function rewriteGatewayRequestForLocalProxy(config) {
  if (!isLocalMachine() || typeof window === "undefined") {
    return config;
  }

  const method = (config.method || "get").toUpperCase();
  if (method !== "POST") {
    return config;
  }

  const requestPath = config.url || "";
  if (!requestPath.startsWith(SERVICEABILITY_PATH_PREFIX)) {
    return config;
  }

  const gatewayBaseUrl = process.env.NEXT_PUBLIC_R2_CLIENT_API_GATEWAY;
  if (!gatewayBaseUrl || !isGatewayBaseUrl(config.baseURL || "", gatewayBaseUrl)) {
    return config;
  }

  config.baseURL = "";
  config.url = `/apigw${requestPath}`;
  attachGatewayAuthHeadersForLocalProxy(config);

  return config;
}

/**
 * Rewrites a single Set-Cookie header from the backend for the local dev host.
 * Strips Domain= so the cookie becomes host-only for dev-redroses.fnpplus.com.
 *
 * @param {string} cookieHeader
 * @returns {string}
 */
export function rewriteSetCookieHeader(cookieHeader) {
  if (!cookieHeader || typeof cookieHeader !== "string") {
    return cookieHeader;
  }

  return cookieHeader
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !/^domain=/i.test(part) && !/^path=/i.test(part))
    .concat("Path=/")
    .join("; ");
}

/**
 * Collects all Set-Cookie values from a fetch Response without collapsing them.
 *
 * @param {Response} response
 * @returns {string[]}
 */
export function extractSetCookieHeaders(response) {
  if (typeof response.headers.getSetCookie === "function") {
    return response.headers.getSetCookie();
  }

  const raw = response.headers.get("set-cookie");
  if (!raw) {
    return [];
  }

  // Fallback for environments without getSetCookie(): split on comma before cookie names.
  return raw.split(/,(?=\s*[^;,=\s]+=)/).map((cookie) => cookie.trim());
}

/**
 * Builds upstream headers for the backend request.
 *
 * @param {Request} request
 * @param {string} backendOrigin
 * @param {boolean} [useGatewayAuth]
 * @returns {Headers}
 */
function buildUpstreamHeaders(request, backendOrigin, useGatewayAuth = false) {
  const upstreamHeaders = new Headers();

  PROXY_FORWARD_HEADERS.forEach((headerName) => {
    const value = request.headers.get(headerName);
    if (value) {
      upstreamHeaders.set(headerName, value);
    }
  });

  if (useGatewayAuth) {
    const forwardedCookie = buildGatewayForwardedCookieHeader(request);
    if (forwardedCookie) {
      upstreamHeaders.set("x-forwarded-cookie", forwardedCookie);
    }

    // API gateway rejects server-side requests when Origin is the gateway host itself (403).
    // Preserve the browser's Origin/Referer from the incoming proxied request.
    const clientOrigin = request.headers.get("origin");
    const clientReferer = request.headers.get("referer");

    if (clientOrigin) {
      upstreamHeaders.set("origin", clientOrigin);
    } else {
      upstreamHeaders.delete("origin");
    }

    if (clientReferer) {
      upstreamHeaders.set("referer", clientReferer);
    } else {
      upstreamHeaders.delete("referer");
    }

    return upstreamHeaders;
  }

  // Control backend may validate origin/referer; align them with the real API host.
  upstreamHeaders.set("origin", backendOrigin);
  upstreamHeaders.set("referer", `${backendOrigin}/`);

  return upstreamHeaders;
}

/**
 * Proxies a request to an upstream backend and forwards the response.
 *
 * @param {Request} request
 * @param {object} options
 * @param {string} options.upstreamBaseUrl
 * @param {string} [options.pathPrefix]
 * @param {string[]} [options.pathSegments]
 * @param {string} [options.errorLabel]
 * @param {boolean} [options.useGatewayAuth]
 * @returns {Promise<Response>}
 */
export async function proxyUpstreamRequest(
  request,
  { upstreamBaseUrl, pathPrefix = "", pathSegments = [], errorLabel = "API", useGatewayAuth = false },
) {
  if (!upstreamBaseUrl) {
    return Response.json({ error: "Upstream base URL is not configured" }, { status: 500 });
  }

  if (!isLocalMachine()) {
    return Response.json({ error: `${errorLabel} proxy is only enabled for local development` }, { status: 404 });
  }

  const method = request.method.toUpperCase();
  const relativePath = [pathPrefix.replace(/^\//, ""), ...pathSegments].filter(Boolean).join("/");
  const requestUrl = new URL(request.url);
  const targetUrl = new URL(relativePath ? `/${relativePath}` : "/", upstreamBaseUrl);
  targetUrl.search = requestUrl.search;

  const backendOrigin = new URL(upstreamBaseUrl).origin;
  const upstreamHeaders = buildUpstreamHeaders(request, backendOrigin, useGatewayAuth);
  const timeoutMs = parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || "5000", 10);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let upstreamResponse;

  try {
    const bodyBuffer = method === "GET" || method === "HEAD" ? null : await request.arrayBuffer();
    const upstreamBody =
      method === "GET" || method === "HEAD" ? undefined : bodyBuffer.byteLength > 0 ? bodyBuffer : new ArrayBuffer(0);

    upstreamResponse = await fetch(targetUrl.toString(), {
      method,
      headers: upstreamHeaders,
      body: upstreamBody,
      redirect: "manual",
      signal: controller.signal,
    });
  } catch (error) {
    clearTimeout(timeoutId);

    if (error?.name === "AbortError") {
      return Response.json({ error: `Upstream ${errorLabel} request timed out` }, { status: 504 });
    }

    return Response.json({ error: `Upstream ${errorLabel} is unavailable` }, { status: 502 });
  } finally {
    clearTimeout(timeoutId);
  }

  const responseHeaders = new Headers();

  PROXY_RESPONSE_HEADERS.forEach((headerName) => {
    const value = upstreamResponse.headers.get(headerName);
    if (value) {
      responseHeaders.set(headerName, value);
    }
  });

  const setCookieHeaders = extractSetCookieHeaders(upstreamResponse);
  setCookieHeaders.forEach((cookieHeader) => {
    responseHeaders.append("set-cookie", rewriteSetCookieHeader(cookieHeader));
  });

  const responseBody = await upstreamResponse.arrayBuffer();

  return new Response(responseBody, {
    status: upstreamResponse.status,
    statusText: upstreamResponse.statusText,
    headers: responseHeaders,
  });
}

/**
 * Proxies a /control/* request to uat-new.fnp.com and forwards the response.
 *
 * @param {Request} request
 * @param {string[]} pathSegments
 * @returns {Promise<Response>}
 */
export async function proxyControlRequest(request, pathSegments = []) {
  return proxyUpstreamRequest(request, {
    upstreamBaseUrl: process.env.NEXT_PUBLIC_BASE_URL,
    pathPrefix: "control",
    pathSegments,
    errorLabel: "control API",
  });
}

/**
 * Proxies an /apigw/serviceability/* request to apigw-uat-r2.fnp.com/serviceability/*.
 *
 * @param {Request} request
 * @param {string[]} pathSegments
 * @returns {Promise<Response>}
 */
export async function proxyServiceabilityRequest(request, pathSegments = []) {
  return proxyUpstreamRequest(request, {
    upstreamBaseUrl: process.env.NEXT_PUBLIC_R2_CLIENT_API_GATEWAY,
    pathPrefix: "serviceability",
    pathSegments,
    errorLabel: "serviceability API",
    useGatewayAuth: true,
  });
}
